<script>
import { cloneDeep, filter } from 'lodash-es';
import { mapState } from 'vuex';

export default {
  name: 'ItineraryDayProgramExtraModal',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    dayProgram: {
      type: Object,
      default: () => {},
    },
    availableExtras: {
      type: Array,
      default: () => [],
    },
    countriesAndAreas: {
      type: Array,
      default: () => [],
    },
    itineraryId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      twentyUrl: `${process.env.TWENTY_FRONT_PAGE}/support-system/extra`,
      toggled: true,
      search: '',
      selectedAreaId: null,
      selectedCategory: null,
      showOnlySelectedExtras: false,
      defaultHeaders: [
        { text: '#', align: 'left', value: 'id' },
        { text: this.$t('itinerary.extraModal.table.name'), align: 'left', value: 'description' },
        { text: this.$t('itinerary.extraModal.table.supplier'), align: 'left', value: 'supplier.name' },
        { text: this.$t('itinerary.extraModal.table.inclusive'), align: 'center', sortable: false },
        { text: this.$t('itinerary.extraModal.table.optional'), align: 'center', sortable: false },
      ],
      pagination: {
        rowsPerPage: 25,
        descending: false,
        sortBy: 'name',
      },
      availableCategories: [
        { id: 1, identifier: 'Excursion' },
        { id: 2, identifier: 'Transfer' },
      ],
      extras: [],
    };
  },
  computed: {
    ...mapState({
      config: state => state.itinerary.dayProgramExtras.config,
      selectedExtraCategory: state => state.itinerary.selectedExtraCategory,
    }),
    getAreasByDayProgramCountryId() {
      const country = filter(this.countriesAndAreas, ca => ca.id === this.dayProgram.countryId);
      return country[0].areas;
    },
    categoryConfig() {
      if (this.selectedCategory && this.config && this.config[this.selectedCategory.id]) {
        return Object.keys(this.config[this.selectedCategory.id])
          .map(key => ({
            ...this.config[this.selectedCategory.id][key],
            key,
          }));
      }
      return [];
    },
    categoryHeaders() {
      const customHeaders = this.categoryConfig.map(header => ({
        text: header.description,
        align: 'center',
        sortable: false,
      }));

      return [
        ...this.defaultHeaders,
        ...customHeaders,
      ];
    },
    filteredCategoryExtras() {
      let filteredExtras = this.extras;

      if (this.selectedCategory) {
        filteredExtras = filteredExtras.filter(extra => extra.extraCategoryId === this.selectedCategory.id);
      }

      if (this.showOnlySelectedExtras) {
        filteredExtras = filteredExtras.filter(extra => extra.type === 'included' || extra.type === 'optional');
      }

      return filteredExtras;
    },
  },
  watch: {
    toggled(value) {
      if (!value) {
        this.$emit('cancel');
      }
    },
    availableExtras(value) {
      this.extras = cloneDeep(value);
    },
  },
  mounted() {
    this.selectedCategory = this.availableCategories.find(availableCategory =>
      availableCategory.identifier.toUpperCase() === this.selectedExtraCategory.toUpperCase());
  },
  methods: {
    update() {
      this.$emit('update', {
        itineraryId: this.itineraryId,
        dayProgramId: this.dayProgram.id,
        extras: this.extras,
      });
    },
    customFilter(items) {
      const search = this.search.toString().toLowerCase();
      return items.filter(i => {
        const supplierName = i.supplier.name.toLowerCase();
        const description = i.content.description.toLowerCase();
        const searchBool =
          !search || (supplierName.indexOf(search) !== -1 || description.indexOf(search) !== -1);
        const selectBool = !this.selectedAreaId || i.areaIds.indexOf(this.selectedAreaId) !== -1;
        return searchBool && selectBool;
      });
    },
    configEntryOptions(options, extra) {
      if (typeof options !== 'undefined') {
        return Object.keys(options).map(key => ({
          value: +key,
          label: options[key],
        }));
      }
      // If no options is set on category config, the values lies on the specific extra
      const extraInfo = extra.content.extraInfo;
      return Object.keys(extraInfo).map(key => ({
        value: +key,
        label: extraInfo[key],
      }));
    },
  },
};
</script>

<template>
  <v-dialog
    v-model="toggled"
    max-width="1100px"
  >
    <v-card>
      <v-flex font-weight-bold pa-3 title>
        <v-icon>directions_walk</v-icon>
        <span>Assign Extras to day program entry</span>
      </v-flex>
      <v-divider light/>
      <v-layout row class="mt-4">
        <v-flex md5>
          <v-text-field
            v-model="search"
            :loading="loading"
            :label="$t('inputs.placeholders.search')"
            class="px-4"
            clear-icon="clear"
            append-icon="search"
          />
        </v-flex>
        <v-flex md5>
          <v-select
            v-model="selectedAreaId"
            :items="getAreasByDayProgramCountryId"
            item-text="name"
            item-value="id"
            label="Filter by area"
            clearable
          />
        </v-flex>
        <v-flex md5>
          <v-select
            v-model="selectedCategory"
            :items="availableCategories"
            item-text="identifier"
            label="Extra category"
            return-object
            class="px-4"
          />
        </v-flex>
      </v-layout>
      <v-layout row>
        <v-checkbox v-model="showOnlySelectedExtras" label="Show only selected extras" class="ml-3"/>
      </v-layout>
      <v-layout row wrap>
        <v-flex xs12>
          <v-progress-linear
            v-if="loading"
            :active="loading"
            :indeterminate="true"
            :height="4"
            class="my-0"
          />
          <v-data-table
            :headers="categoryHeaders"
            :items="filteredCategoryExtras"
            :loading="loading"
            :pagination.sync="pagination"
            :search="search"
            :custom-filter="customFilter"
            class="elevation-1"
          >
            <template v-slot:items="props">
              <td>{{ props.item.id }}</td>
              <td>
                <a
                  :href="`${ twentyUrl }/${ props.item.id }/edit`"
                  target="_blank"
                >
                  {{ props.item.content.description }}
                </a>
              </td>
              <td>{{ props.item.supplier.name }}</td>
              <td>
                <v-checkbox
                  v-model="props.item.type"
                  hide-details
                  value="included"
                  class="justify-center"
                />
              </td>
              <td>
                <v-checkbox
                  v-model="props.item.type"
                  hide-details
                  value="optional"
                  class="justify-center"
                />
              </td>
              <template v-if="categoryConfig">
                <td
                  v-for="(entry) in categoryConfig"
                  :key="`category-option-${entry.key}`"
                >
                  <v-select
                    v-model="props.item[entry.key]"
                    :items="configEntryOptions(entry.options, props.item)"
                    :multiple="entry.multiSelect"
                    class="px-4"
                    clearable
                    item-text="label"
                    item-value="value"
                  />
                </td>
              </template>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
      <v-layout justify-end>
        <v-btn
          color="red darken-1"
          flat
          @click="toggled = false"
        >
          {{ $t('inputs.actions.cancel') }}
        </v-btn>
        <v-btn
          :disabled="loading"
          :loading="loading"
          color="blue darken-1"
          flat
          @click="update"
        >
          {{ $t('inputs.actions.save') }}
        </v-btn>
      </v-layout>
    </v-card>
  </v-dialog>
</template>



// WEBPACK FOOTER //
// src/components/itinerary/ItineraryDayProgramExtraModal.vue